import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Input, Typography, message as antdMessage } from "antd";
import { RobotOutlined, SendOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { AiAgentChatResponse, useAiAgentChat } from "../services/aiagent.service";

const { Text } = Typography;

const CARD_BG = "#ffffff";
const CARD_BORDER = "#e8eaf0";
const TEXT_PRIMARY = "#1e293b";
const TEXT_MUTED = "#64748b";
const ACCENT = "#6366f1";

const AiAgentWidget = () => {
  const navigate = useNavigate();
  const [question, setQuestion] = useState("");
  const [reply, setReply] = useState<string | null>(null);
  const [hasPendingAction, setHasPendingAction] = useState(false);

  const chatMutation = useAiAgentChat();

  const handleAsk = () => {
    const trimmed = question.trim();
    if (!trimmed || chatMutation.isLoading) return;

    chatMutation.mutate(
      { message: trimmed },
      {
        onSuccess: (res: any) => {
          if (res?.statusCode !== 200 && res?.statusCode !== 201) {
            antdMessage.error(res?.statusMessage || "The AI agent could not process that request.");
            return;
          }
          const data: AiAgentChatResponse = res.data;
          setReply(data.reply);
          setHasPendingAction((data.pendingActions?.length ?? 0) > 0);
        },
        onError: (error: any) => {
          antdMessage.error(error?.response?.statusMessage || "Something went wrong reaching the AI agent.");
        },
      }
    );
  };

  return (
    <Card
      title={
        <span style={{ color: TEXT_PRIMARY, fontWeight: 600 }}>
          <RobotOutlined style={{ color: ACCENT, marginRight: 8 }} />
          AI Agent
        </span>
      }
      extra={
        <Button type="link" onClick={() => navigate("/ai-agent")} style={{ color: ACCENT, paddingRight: 0 }}>
          Open full chat <ArrowRightOutlined />
        </Button>
      }
      style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}`, borderRadius: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}
      headStyle={{ background: CARD_BG, borderBottom: `1px solid ${CARD_BORDER}`, color: TEXT_PRIMARY }}
      bodyStyle={{ background: CARD_BG, paddingTop: 12 }}
    >
      <Text style={{ color: TEXT_MUTED, display: "block", marginBottom: 12 }}>
        Ask about trips, travellers, attendants, finance, or analytics.
      </Text>

      <div style={{ display: "flex", gap: 8, marginBottom: reply ? 12 : 0 }}>
        <Input
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onPressEnter={handleAsk}
          placeholder="e.g. How many trips are active today?"
          disabled={chatMutation.isLoading}
        />
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={handleAsk}
          loading={chatMutation.isLoading}
          disabled={!question.trim()}
          style={{ background: ACCENT, border: "none" }}
        />
      </div>

      {reply && (
        <div
          style={{
            background: "#f5f6fa",
            borderRadius: 10,
            padding: "10px 14px",
            color: TEXT_PRIMARY,
            whiteSpace: "pre-wrap",
            lineHeight: 1.5,
          }}
        >
          {reply}
          {hasPendingAction && (
            <div style={{ marginTop: 8 }}>
              <Button size="small" type="link" onClick={() => navigate("/ai-agent")} style={{ color: ACCENT, paddingLeft: 0 }}>
                This proposes an action -- open the full AI Agent to confirm <ArrowRightOutlined />
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default AiAgentWidget;
